import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import { getApiUrl } from '../../config/api';

export interface AutoSyncSettings {
  auto_sync_enabled: boolean;
  sync_interval_hours: number;
}

export interface AutoSyncStatus {
  config_id: number;
  config_name: string;
  auto_sync_enabled: boolean;
  sync_interval_hours: number;
  last_auto_sync: string | null;
  next_sync_estimated: string | null;
}

export interface AutoSyncResponse {
  success: boolean;
  message: string;
  config_id: number;
  auto_sync_enabled: boolean;
  sync_interval_hours: number;
}

export const autoSyncApi = createApi({
  reducerPath: 'autoSyncApi',
  baseQuery: fetchBaseQuery({
    baseUrl: getApiUrl('/api/v1/auto-sync'),
    prepareHeaders: (headers, { getState }) => {
      const token = (getState() as any).auth.token;
      if (token) {
        headers.set('authorization', `Bearer ${token}`);
      }
      return headers;
    },
  }),
  tagTypes: ['AutoSync'],
  endpoints: (builder) => ({
    getAutoSyncStatus: builder.query<AutoSyncStatus, number>({
      query: (configId) => `/configurations/${configId}/auto-sync`,
      providesTags: ['AutoSync'],
    }),
    updateAutoSyncSettings: builder.mutation<AutoSyncResponse, { configId: number; settings: AutoSyncSettings }>({
      query: ({ configId, settings }) => ({
        url: `/configurations/${configId}/auto-sync`,
        method: 'POST',
        body: settings,
      }),
      invalidatesTags: ['AutoSync'],
    }),
    triggerManualSync: builder.mutation<{ success: boolean; message: string; config_id: number }, number>({
      query: (configId) => ({
        url: `/configurations/${configId}/auto-sync/trigger`,
        method: 'POST',
      }),
    }),
    getSchedulerStatus: builder.query<{ scheduler_running: boolean; active_tasks: number; running_configs: number[] }, void>({
      query: () => '/status',
    }),
  }),
});

export const {
  useGetAutoSyncStatusQuery,
  useUpdateAutoSyncSettingsMutation,
  useTriggerManualSyncMutation,
  useGetSchedulerStatusQuery,
} = autoSyncApi;
